/**
 * Application shell and routing.
 *
 * The route table is deliberately a switch over a parsed hash. There are six destinations;
 * a router dependency would be more configuration than code, and this stays readable.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { Shell } from '@/app/Shell'
import { SnapshotBanner } from '@/components/PeriodSwitcher'
import { sectionOf } from '@/app/routes'
import { useSession } from '@/app/session'
import { useRoute } from '@/app/useRoute'
import { SignIn } from '@/features/auth/SignIn'
import { Dashboard } from '@/features/dashboard/Dashboard'
import { Application, Inbox } from '@/features/inbox/Inbox'
import { PackageWorkspace } from '@/features/packages/PackageWorkspace'
import { Ingestion } from '@/features/ingest/Ingestion'
import { NewEntry } from '@/features/registers/NewEntry'
import { PortfolioRegister } from '@/features/registers/PortfolioRegister'
import { RowHistory } from '@/features/registers/RowHistory'
import { FormBuilder } from '@/features/builder/FormBuilder'
import { Periods } from '@/features/periods/Periods'
import { Project, ProjectList } from '@/features/projects/Projects'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Portfolio figures change at ingestion and at month close, not by the second. A
      // minute of staleness costs nothing and saves a request on every tab switch.
      staleTime: 60_000,
      // A 401 has already been through one token refresh in the client. Retrying past that
      // just delays telling the user their session ended.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || status === 404) return false
        return failureCount < 2
      },
      refetchOnWindowFocus: false,
    },
  },
})

function Screen() {
  const route = useRoute()

  switch (route.name) {
    case 'projects':
      return <ProjectList />
    case 'project':
      return <Project projectId={route.projectId} />
    case 'package':
      return <PackageWorkspace packageId={route.packageId} tab={route.tab} />
    case 'inbox':
      return <Inbox />
    case 'application':
      return <Application applicationId={route.applicationId} />
    case 'periods':
      return <Periods />
    case 'ingestion':
      return <Ingestion />
    case 'builder':
      return <FormBuilder slug={route.slug} />
    case 'register':
      return <PortfolioRegister slug={route.slug} />
    case 'history':
      return <RowHistory slug={route.slug} rowId={route.rowId} />
    case 'create':
      return <NewEntry />
    default:
      return <Dashboard />
  }
}

function SignedIn() {
  const route = useRoute()
  return (
    <Shell active={sectionOf(route)}>
      {/* Above every screen, because forgetting you are in a sealed month is the mistake
          this product most needs to prevent. */}
      <SnapshotBanner />
      <Screen />
    </Shell>
  )
}

export function App() {
  const status = useSession((state) => state.status)

  return (
    <QueryClientProvider client={queryClient}>
      {status === 'signed-in' ? <SignedIn /> : <SignIn />}
    </QueryClientProvider>
  )
}
